import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import ArrowDownLineSvg from '../../../public/images/common/home/2508/arrow-down-line.svg'
import styles from './index.module.scss'

type ServiceCard = {
  title: string
  description: string
  imageUrl: string
  linkUrl: string
  imageWidthMd: string
  imageWidthLg: string
  imagePosition?: 'center' | 'right'
}

export const TopPageForStartups2508: FC = observer(() => {
  const { t } = useTranslation(['index'])
  const router = useRouter()
  const [isMobileClient, setIsMobileClient] = useState(false)

  useEffect(() => {
    const mobileBreakpoint = 1092
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return
      }
      setIsMobileClient(window.innerWidth < mobileBreakpoint)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const services: (ServiceCard & { category: 'equity' | 'debt' | 'assets' | 'others' })[] = [
    {
      category: 'equity',
      title: t('topPage2508.startups.services.angelMatching.title'),
      description: t('topPage2508.startups.services.angelMatching.description'),
      imageUrl: '/images/common/home/2508/マッチング.webp',
      linkUrl: '/angels',
      imageWidthMd: '246px',
      imageWidthLg: '100%',
    },
    {
      category: 'equity',
      title: t('topPage2508.startups.services.investorDatabase.title'),
      description: t('topPage2508.startups.services.investorDatabase.description'),
      imageUrl: '/images/common/home/2508/TOSHIKADB.webp',
      linkUrl: 'https://invdb.protocol.ooo/',
      imageWidthMd: '251.8px',
      imageWidthLg: '287.3px',
    },
    {
      category: 'equity',
      title: t('topPage2508.startups.services.fundingSupport.title'),
      description: t('topPage2508.startups.services.fundingSupport.description'),
      imageUrl: '/images/common/home/2508/資金調達フルサポート.webp',
      linkUrl: 'https://toshika-lp.protocol.ooo/toshika',
      imageWidthMd: '236px',
      imageWidthLg: '270px',
    },
    {
      category: 'debt',
      title: t('topPage2508.startups.services.ventureDebt.title'),
      description: t('topPage2508.startups.services.ventureDebt.description'),
      imageUrl: '/images/common/home/2508/デット.webp',
      linkUrl: 'https://toshika-lp.protocol.ooo/protocol-startup-debt',
      imageWidthMd: '258px',
      imageWidthLg: '295px',
      imagePosition: 'right',
    },
    {
      category: 'assets',
      title: t('topPage2508.startups.services.receivableGuarantee.title'),
      description: t('topPage2508.startups.services.receivableGuarantee.description'),
      imageUrl: '/images/common/home/2508/売掛債権補償.webp',
      linkUrl: 'https://toshika-lp.protocol.ooo/protocol-deal',
      imageWidthMd: '251.39px',
      imageWidthLg: '287.3px',
    },
    {
      category: 'assets',
      title: t('topPage2508.startups.services.debtSecuritization.title'),
      description: t('topPage2508.startups.services.debtSecuritization.description'),
      imageUrl: '/images/common/home/2508/債権流動化.webp',
      linkUrl: 'https://toshika-lp.protocol.ooo/protocol-irm',
      imageWidthMd: '200px',
      imageWidthLg: '211px',
    },
    {
      category: 'others',
      title: t('topPage2508.startups.services.ma.title'),
      description: t('topPage2508.startups.services.ma.description'),
      imageUrl: '/images/common/home/2508/M&A.webp',
      linkUrl: `https://ma.protocol.ooo/${router.locale === 'ja' ? '1' : '1-1'}`,
      imageWidthMd: '192px',
      imageWidthLg: '226px',
    },
    {
      category: 'others',
      title: t('topPage2508.startups.services.subsidies.title'),
      description: t('topPage2508.startups.services.subsidies.description'),
      imageUrl: '/images/common/home/2508/補助金.webp',
      linkUrl: '/subsidies',
      imageWidthMd: '292px',
      imageWidthLg: '295px',
      imagePosition: 'right',
    },
    {
      category: 'others',
      title: t('topPage2508.startups.services.startupServices.title'),
      description: t('topPage2508.startups.services.startupServices.description'),
      imageUrl: '/images/common/home/2508/スタートアップ向けサービス.webp',
      linkUrl: 'https://prosupporters.protocol.ooo/',
      imageWidthMd: '258px',
      imageWidthLg: '289px',
    },
  ]

  const equityServices = services.filter((service) => service.category === 'equity')
  const debtServices = services.filter((service) => service.category === 'debt')
  const assetServices = services.filter((service) => service.category === 'assets')
  const otherServices = services.filter((service) => service.category === 'others')

  const renderServiceCard = (service: ServiceCard & { category: 'equity' | 'debt' | 'assets' | 'others' }) => {
    const categoryClassMap: Record<'equity' | 'debt' | 'assets' | 'others', string> = {
      equity: styles.cardEquity,
      debt: styles.cardDebt,
      assets: styles.cardAssets,
      others: styles.cardOthers,
    }

    const categoryClass = categoryClassMap[service.category]

    const cardButton = (
      <span className={styles.cardButton}>
        <span>{t('topPage2508.common.detailButton')}</span>
        <ArrowDownLineSvg />
      </span>
    )

    const card = (
      <div className={`${styles.serviceCard} ${categoryClass}`}>
        <div className={styles.cardInner}>
          <div className={styles.cardTexts}>
            <h3 className={styles.cardTitle}>{service.title}</h3>
            <p className={styles.cardDescription}>{service.description}</p>
            {isMobileClient && cardButton}
          </div>
          <div
            className={`${styles.cardImageWrap} ${
              service.imagePosition === 'right' ||
              (service.title === t('topPage2508.startups.services.angelMatching.title') && isMobileClient)
                ? styles.cardImageWrapRight
                : ''
            }`}
            style={{
              width: isMobileClient ? service.imageWidthMd : service.imageWidthLg,
            }}
          >
            <img src={service.imageUrl} alt={service.title} />
          </div>
          {!isMobileClient && cardButton}
        </div>
      </div>
    )

    if (service.linkUrl.startsWith('http')) {
      return (
        <a
          key={service.title}
          href={service.linkUrl}
          target='_blank'
          rel='noopener noreferrer'
          className={styles.cardLink}
        >
          {card}
        </a>
      )
    }

    return (
      <Link key={service.title} href={service.linkUrl}>
        <a className={styles.cardLink}>{card}</a>
      </Link>
    )
  }

  return (
    <div className={styles.container}>
      <div className='container'>
        <div className={styles.header}>
          <h1 className={styles.mainTitle}>{t('topPage2508.startups.sectionTitle')}</h1>
          <p className={styles.subtitle}>{t('topPage2508.startups.sectionSubtitle')}</p>
        </div>

        <div className={styles.mainBox}>
          {/* エクイティセクション */}
          <div className={styles.categorySection}>
            <h2 className={styles.categoryTitleEquity}>{t('topPage2508.startups.categories.equity')}</h2>
            <div className={styles.categoryGrid}>{equityServices.map(renderServiceCard)}</div>
          </div>

          {/* デット・アセットセクション */}
          <div className={styles.debtAssetsWrapper}>
            <div className={styles.categorySection}>
              <h2 className={styles.categoryTitleDebt}>{t('topPage2508.startups.categories.debt')}</h2>
              <div className={styles.debtGrid}>{debtServices.map(renderServiceCard)}</div>
            </div>
            <div className={styles.categorySection}>
              <h2 className={styles.categoryTitleAssets}>{t('topPage2508.startups.categories.assets')}</h2>
              <div className={styles.assetsGrid}>{assetServices.map(renderServiceCard)}</div>
            </div>
          </div>

          {/* その他セクション */}
          <div className={styles.categorySection}>
            <h2 className={styles.categoryTitleOthers}>{t('topPage2508.startups.categories.others')}</h2>
            <div className={styles.categoryGrid}>{otherServices.map(renderServiceCard)}</div>
          </div>

          {/* 戦略会議バナー */}
          <div className={styles.categorySection}>
            <h2 className={styles.categoryTitleFunding}>{t('topPage2508.startups.categories.fundingStrategy')}</h2>
            <div className={styles.strategyBanner}>
              <div className={styles.strategyContent}>
                <a
                  href='https://toshika-lp.protocol.ooo/funding-strategy-conference'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <span className={styles.cardButton}>
                    <span>{t('topPage2508.common.detailButton')}</span>
                    <ArrowDownLineSvg />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})
